import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Activity,
  Briefcase,
  CheckCircle2,
  Clock,
  DollarSign,
  Filter,
  Layers,
  RefreshCw,
  Users,
  Wallet
} from 'lucide-react';
import api, { API_ENDPOINTS } from '../config/api';

const RANGE_OPTIONS = [
  { value: '1d', label: '1 day' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '60d', label: '60 days' },
  { value: '90d', label: '90 days' },
  { value: '1y', label: '1 year' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom' }
];

const formatUsd = (value) => {
  const n = Number(value) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2
  }).format(n);
};

const formatCount = (value) =>
  new Intl.NumberFormat('en-US').format(Number(value) || 0);

const toInputDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const StatCard = ({ icon: Icon, label, value, sub, accent = false }) => (
  <div
    className={`rounded-xl border p-4 ${
      accent
        ? 'border-accent/30 bg-accent/5'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40'
    }`}
  >
    <div className="flex items-start justify-between gap-3 mb-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <div className="w-8 h-8 rounded-lg bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="text-2xl font-semibold text-heading tabular-nums">{value}</p>
    {sub ? (
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{sub}</p>
    ) : null}
  </div>
);

const StageCard = ({ title, count, amount, icon: Icon }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900/40">
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-accent" />
      <h4 className="text-sm font-semibold text-heading">{title}</h4>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Count</p>
        <p className="text-xl font-semibold text-heading tabular-nums">
          {formatCount(count)}
        </p>
      </div>
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Amount</p>
        <p className="text-xl font-semibold text-heading tabular-nums">
          {formatUsd(amount)}
        </p>
      </div>
    </div>
  </div>
);

/**
 * Deployer platform data room — filterable operational metrics.
 */
const DeployerDataRoom = ({ enabled = true }) => {
  const [range, setRange] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [appliedCustom, setAppliedCustom] = useState({ from: '', to: '' });

  const queryParams = useMemo(() => {
    if (range === 'custom') {
      if (!appliedCustom.from) return null;
      return {
        range: 'custom',
        from: appliedCustom.from,
        ...(appliedCustom.to ? { to: appliedCustom.to } : {})
      };
    }
    return { range };
  }, [range, appliedCustom]);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch
  } = useQuery(
    ['deployerPlatformStats', queryParams],
    () =>
      api.get(API_ENDPOINTS.DEPLOYER.PLATFORM_STATS, {
        params: queryParams
      }),
    {
      enabled: Boolean(enabled && queryParams),
      select: (res) => res.data,
      keepPreviousData: true,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false
    }
  );

  const applyCustom = () => {
    if (!customFrom) return;
    if (customTo && customFrom > customTo) return;
    setAppliedCustom({ from: customFrom, to: customTo });
  };

  const rangeLabel = useMemo(() => {
    if (!data?.range) return '';
    if (data.range.preset === 'all') return 'All time';
    if (data.range.preset === 'custom') {
      return `${toInputDate(data.range.from)} → ${toInputDate(data.range.to)}`;
    }
    return RANGE_OPTIONS.find((o) => o.value === data.range.preset)?.label || data.range.preset;
  }, [data]);

  return (
    <div className="card p-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <Activity className="w-5 h-5 text-accent mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-heading">Platform Data Room</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Operational metrics across users, listings, approvals, and escrow project flow.
              {rangeLabel ? (
                <span className="block mt-1 text-xs">
                  Showing: <span className="font-medium text-heading">{rangeLabel}</span>
                </span>
              ) : null}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
              <Filter className="w-3.5 h-3.5" />
              Time range
            </label>
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="input-field min-w-[140px]"
            >
              {RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {range === 'custom' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  From
                </label>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  To
                </label>
                <input
                  type="date"
                  value={customTo}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="input-field"
                />
              </div>
              <button
                type="button"
                onClick={applyCustom}
                disabled={!customFrom || (customTo && customFrom > customTo)}
                className="btn-primary disabled:opacity-50"
              >
                Apply
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="btn-outline inline-flex items-center gap-2"
            title="Refresh stats"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {range === 'custom' && !queryParams && (
        <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          Select a start date and click Apply to load custom-range stats.
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {error.response?.data?.error || 'Failed to load platform stats'}
        </div>
      )}

      {isLoading && !data ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              label="Users"
              value={formatCount(data.users?.count)}
              sub="New accounts in range"
            />
            <StatCard
              icon={Briefcase}
              label="Jobs & Gigs"
              value={formatCount(data.listings?.total)}
              sub={`${formatCount(data.listings?.jobs)} jobs · ${formatCount(data.listings?.gigs)} gigs`}
            />
            <StatCard
              icon={Users}
              label="Clients"
              value={formatCount(data.posters?.clients)}
              sub="Unique users who posted jobs"
            />
            <StatCard
              icon={Layers}
              label="Freelancers"
              value={formatCount(data.posters?.freelancers)}
              sub="Unique users who posted gigs"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              icon={CheckCircle2}
              label="Approved jobs"
              value={formatCount(data.approved?.jobs)}
              sub="Applications approved in range"
            />
            <StatCard
              icon={CheckCircle2}
              label="Approved gigs"
              value={formatCount(data.approved?.gigs)}
              sub="Orders approved in range"
            />
            <StatCard
              icon={CheckCircle2}
              label="Approved total"
              value={formatCount(data.approved?.total)}
              sub="Jobs + gigs approved"
            />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-heading mb-3">
              Escrow project flow
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <StageCard
                title="Deposited"
                count={data.projects?.deposited?.count}
                amount={data.projects?.deposited?.amountUSD}
                icon={Wallet}
              />
              <StageCard
                title="In progress"
                count={data.projects?.inProgress?.count}
                amount={data.projects?.inProgress?.amountUSD}
                icon={Clock}
              />
              <StageCard
                title="Completed"
                count={data.projects?.completed?.count}
                amount={data.projects?.completed?.amountUSD}
                icon={Briefcase}
              />
              <StageCard
                title="Confirmed"
                count={data.projects?.confirmed?.count}
                amount={data.projects?.confirmed?.amountUSD}
                icon={CheckCircle2}
              />
            </div>
          </div>

          <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-accent text-white flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-heading">
                    Total payments processed
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Sum of amounts across deposited, in-progress, and completed events in
                    this range — excluding confirmed.
                  </p>
                </div>
              </div>
              <div className="text-left sm:text-right">
                <p className="text-3xl font-bold text-heading tabular-nums">
                  {formatUsd(data.totalPaymentsProcessed?.amountUSD)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {formatCount(data.totalPaymentsProcessed?.count)} projects
                </p>
              </div>
            </div>
          </div>

          {data.generatedAt && (
            <p className="text-xs text-gray-400 text-right">
              Updated {new Date(data.generatedAt).toLocaleString()}
              {isFetching ? ' · refreshing…' : ''}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default DeployerDataRoom;
